<template>
  <div v-if="loading || stats">
    <div v-if="loading" class="d-flex justify-center pa-4">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else-if="stats">
      <v-row>
        <!-- Left: KPIs -->
        <v-col cols="12" md="3">
          <v-row dense>
            <v-col cols="12">
              <v-card outlined class="text-center pa-2 mb-2">
                <div class="text-subtitle-2 grey--text text-uppercase">Total Requêtes</div>
                <div class="text-h4 font-weight-bold primary--text">
                  {{ formatNumber(stats.total_requests) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="text-center pa-2 mb-2">
                <div class="text-subtitle-2 grey--text text-uppercase">URLs Uniques</div>
                <div class="text-h4 font-weight-bold">
                  {{ formatNumber(stats.unique_paths) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="text-center pa-2">
                <div class="text-subtitle-2 grey--text text-uppercase">Top Bot</div>
                <div class="text-h5 font-weight-bold text-truncate">
                  {{ topBotName }}
                </div>
                <div class="text-caption">{{ topBotPercent }}% des requêtes</div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>

        <!-- Middle: Top Sites -->
        <v-col cols="12" md="6">
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
              TOP SITES (TOP 5)
            </div>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  :loading="exportLoading"
                  :disabled="exportLoading"
                  v-bind="attrs"
                  v-on="on"
                  @click="downloadTopSites"
                >
                  <v-icon size="18">
                    {{ icons.mdiDownload }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Télécharger l'Excel</span>
            </v-tooltip>
          </div>

          <div
            v-for="item in stats.top_sites"
            :key="`${item.site_id}-${item.site_domain || item.site_name || 'site'}`"
            class="mb-4"
          >
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span class="font-weight-medium text-truncate pr-2">
                {{ getTopSiteLabel(item) }}
              </span>
              <span class="grey--text text--darken-1">{{ formatNumber(item.count) }} <span class="text-caption ml-1">({{ getPercent(item.count) }}%)</span></span>
            </div>
            <v-progress-linear
              :value="getPercent(item.count)"
              color="primary"
              height="10"
              rounded
              background-opacity="0.1"
            ></v-progress-linear>
          </div>
          <div v-if="!stats.top_sites || stats.top_sites.length === 0" class="text-body-2 grey--text">
            Aucun site pour les filtres courants.
          </div>
        </v-col>

        <!-- Right: Bot Distribution -->
        <v-col cols="12" md="3">
          <div class="text-subtitle-1 font-weight-bold mb-4 grey--text text--darken-2">
            RÉPARTITION PAR BOT (TOP 5)
          </div>
          <div v-for="item in stats.bot_distribution" :key="item.bot_name" class="mb-4">
            <div class="d-flex justify-space-between text-body-2 mb-1">
              <span class="font-weight-medium text-truncate pr-2">{{ item.bot_name || "-" }}</span>
              <span class="grey--text text--darken-1">{{ formatNumber(item.count) }} <span class="text-caption ml-1">({{ getPercent(item.count) }}%)</span></span>
            </div>
            <v-progress-linear
              :value="getPercent(item.count)"
              color="primary"
              height="10"
              rounded
              background-opacity="0.1"
            ></v-progress-linear>
          </div>
          <div v-if="!stats.bot_distribution || stats.bot_distribution.length === 0" class="text-body-2 grey--text">
            Aucun bot pour les filtres courants.
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import { mdiDownload } from "@mdi/js";
import { ref, watch, computed } from "@vue/composition-api";

export default {
  name: "TrafficStats",
  props: {
    params: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const loading = ref(false);
    const exportLoading = ref(false);
    const stats = ref(null);

    const paramsSerializer = (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach((v) => searchParams.append(key, v));
          }
        } else if (value !== null && value !== undefined) {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    };

    const fetchStats = async () => {
      if (!props.isVisible) return;

      loading.value = true;
      try {
        const response = await axios.get("/geo/traffic/stats", {
          params: props.params,
          paramsSerializer,
        });
        stats.value = response.data;
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        loading.value = false;
      }
    };

    const downloadTopSites = async () => {
      exportLoading.value = true;
      try {
        const { data } = await axios.get("/geo/traffic/top-sites/export", {
          params: props.params,
          paramsSerializer,
          responseType: "blob",
        });

        const downloadLink = document.createElement("a");
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const objectUrl = URL.createObjectURL(blob);
        downloadLink.href = objectUrl;
        downloadLink.download = "geo-traffic-top-sites.xlsx";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Error exporting top sites:", error);
      } finally {
        exportLoading.value = false;
      }
    };

    watch(() => props.isVisible, (val) => {
      if (val) fetchStats();
    }, { immediate: true });

    watch(() => props.params, () => {
      if (props.isVisible) fetchStats();
    }, { deep: true });

    const formatNumber = (num) => {
      return new Intl.NumberFormat("fr-FR").format(num || 0);
    };

    const getPercent = (count) => {
      if (!stats.value || !stats.value.total_requests) return 0;
      return Math.round((count / stats.value.total_requests) * 100);
    };

    const getTopSiteLabel = (item) => {
      return item.site_name || item.site_domain || `Site #${item.site_id}`;
    };

    const topBotName = computed(() => {
      if (
        stats.value &&
        Array.isArray(stats.value.bot_distribution) &&
        stats.value.bot_distribution.length > 0
      ) {
        return stats.value.bot_distribution[0].bot_name;
      }
      return "-";
    });

    const topBotPercent = computed(() => {
      if (
        stats.value &&
        stats.value.total_requests > 0 &&
        Array.isArray(stats.value.bot_distribution) &&
        stats.value.bot_distribution.length > 0
      ) {
        return getPercent(stats.value.bot_distribution[0].count);
      }
      return 0;
    });

    return {
      icons: {
        mdiDownload,
      },
      exportLoading,
      loading,
      stats,
      formatNumber,
      getPercent,
      getTopSiteLabel,
      topBotName,
      topBotPercent,
      downloadTopSites,
    };
  },
};
</script>
